import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gosafeApi } from "../../api";
import {
  loadingDoctorsSelect,
  loadedDoctorsSelect,
  failedDoctorsSelect,
  loading,
  loaded,
  errorMessage,
  successMessage,
  loadingShiftProcedures,
  loadedShiftProcedures,
  failedShiftProcedures,
  addShiftProcedure,
  changeParams,
  resetParams,
  changePageSize,
  changePage,
} from "../../store/secretary/shift_procedure";
import { translations, replaceEmptyToNull } from "../../helpers";

export const useShiftProcedureStore = () => {
  const dispatch = useDispatch();

  const {
    doctorsSelect,
    shiftTypes,
    procedureTypes,
    anesthesiaTechniques,
    message,
    isLoading,
    shiftProcedures,
    params,
    pageSize,
    page,
    totalPages,
  } = useSelector((state) => state.shiftProcedure);

  const getDoctorsBySelect = useCallback(async () => {
    try {
      dispatch(loadingDoctorsSelect());

      const { data } = await gosafeApi.get("doctor/bySelect");

      dispatch(loadedDoctorsSelect(data));
      return;
    } catch (error) {
      dispatch(
        failedDoctorsSelect(
          translations[error?.response?.data?.message] ||
            error?.response?.data?.message ||
            error?.message
        )
      );
      return;
    }
  }, [dispatch]);

  const startAddShiftProcedure = useCallback(
    async (formData) => {
      try {
        dispatch(loading());

        const body = processFormData(formData);
        const newBody = buildFinalBody(body);

        // console.log("newBody", newBody);

        const { data } = await gosafeApi.post("shiftProcedure", newBody);

        dispatch(addShiftProcedure(data.shiftProcedure));
        dispatch(successMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          errorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startGetAnesthesiaQuestions = useCallback(
    async (technique) => {
      try {
        dispatch(loading());

        const { data } = await gosafeApi.get("anesthesiaQuestions", {
          params: { technique },
        });

        dispatch(loaded());

        return data.anesthesiaQuestions;
      } catch (error) {
        dispatch(loaded());
        return (
          translations[error?.response?.data?.message] ||
          error?.response?.data?.message ||
          error?.message
        );
      }
    },
    [dispatch]
  );

  const startGetShiftProcedures = useCallback(
    async (params, page, pageSize) => {
      try {
        dispatch(loadingShiftProcedures());

        const filteredParams = Object.fromEntries(
          Object.entries({
            ...params,
            startProcedureTime: params.startProcedureTime
              ? `${params.startProcedureTime}T00:00:00.000Z`
              : "",
            endProcedureTime: params.endProcedureTime
              ? `${params.endProcedureTime}T23:59:59.000Z`
              : "",
          }).filter(([_, value]) => value !== "")
        );

        // console.log("params", params);
        // console.log("filteredParams", filteredParams);

        const { data } = await gosafeApi.get("shiftProcedure", {
          params: {
            ...filteredParams,
            page,
            pageSize,
          },
        });

        dispatch(loadedShiftProcedures(data));
        return;
      } catch (error) {
        dispatch(
          failedShiftProcedures(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch]
  );

  const startShiftProcedureFilter = useCallback(
    async (params) => {
      try {
        dispatch(changeParams(params));
        const { startProcedureTime, endProcedureTime } = params;

        if (
          (startProcedureTime && !endProcedureTime) ||
          (!startProcedureTime && endProcedureTime)
        ) {
          throw new Error(
            "Si proporciona una fecha inicial, también debe proporcionar una fecha final, y viceversa."
          );
        }

        if (
          startProcedureTime &&
          endProcedureTime &&
          new Date(endProcedureTime) < new Date(startProcedureTime)
        ) {
          throw new Error(
            "La fecha final no puede ser menor que la fecha inicial."
          );
        }
        await startGetShiftProcedures(params, page, pageSize);
        return;
      } catch (error) {
        dispatch(
          failedShiftProcedures(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch, startGetShiftProcedures, page, pageSize]
  );

  const startResetParams = useCallback(async () => {
    try {
      dispatch(resetParams());
      await startGetShiftProcedures({}, page, pageSize);
      return;
    } catch (error) {
      dispatch(
        errorMessage(
          translations[error?.response?.data?.message] ||
            error?.response?.data?.message ||
            error?.message
        )
      );
      return;
    }
  }, [dispatch, startGetShiftProcedures, page, pageSize]);

  const startChangePageSize = useCallback(
    async (pageSize) => {
      dispatch(changePageSize(pageSize));
      await startGetShiftProcedures(params, page, pageSize);
    },
    [dispatch, startGetShiftProcedures, params, page]
  );

  const startChangePage = useCallback(
    async (page) => {
      dispatch(changePage(page));
      await startGetShiftProcedures(params, page, pageSize);
    },
    [dispatch, startGetShiftProcedures, params, pageSize]
  );

  const startAddPage = useCallback(async () => {
    if (totalPages > parseInt(page)) {
      const newPage = (parseInt(page) + 1).toString();
      dispatch(changePage(newPage));
      await startGetShiftProcedures(params, newPage, pageSize);
    }
  }, [dispatch, startGetShiftProcedures, params, page, pageSize, totalPages]);

  const startRemovePage = useCallback(async () => {
    if (parseInt(page) > 1) {
      const newPage = (parseInt(page) - 1).toString();
      dispatch(changePage(newPage));
      await startGetShiftProcedures(params, newPage, pageSize);
    }
  }, [dispatch, startGetShiftProcedures, params, page, pageSize, totalPages]);

  const reloadShiftProcedures = useCallback(async () => {
    await startGetShiftProcedures(params, page, pageSize);
  }, [startGetShiftProcedures, params, page, pageSize]);

  const processFormData = (data) => {
    const anesthesiaAnswers = [];
    const body = {};

    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

    for (const key in data) {
      if (uuidPattern.test(key)) {
        anesthesiaAnswers.push({
          questionId: key,
          answerValue: data[key],
        });
      } else {
        body[key] = data[key];
      }
    }

    return {
      ...body,
      anesthesiaAnswers,
    };
  };

  const buildFinalBody = (rawBody) => {
    const { hasComplications, procedureTime, ...rest } = rawBody;

    return {
      ...rest,
      hasComplications: hasComplications === "true",
      procedureTime: new Date(procedureTime).toISOString(),
    };
  };

  return {
    // Props
    doctorsSelect,
    shiftTypes,
    procedureTypes,
    anesthesiaTechniques,
    message,
    isLoading,
    shiftProcedures,
    params,
    pageSize,
    page,
    totalPages,

    // Methods
    getDoctorsBySelect,
    startGetAnesthesiaQuestions,
    startAddShiftProcedure,
    startGetShiftProcedures,
    startShiftProcedureFilter,
    startResetParams,
    startChangePageSize,
    startChangePage,
    startAddPage,
    startRemovePage,
    reloadShiftProcedures,
  };
};
